import React, { createContext, useContext, useState, useEffect } from 'react';
import { authHelpers } from '../lib/supabaseClient';
import supabase from '../lib/supabaseClient';

const AuthContext = createContext({
  user: null,
  userProfile: null,
  subscription: null,
  loading: true,
  signOut: () => {},
  hasSubscriptionAccess: () => false,
  getSubscriptionStatus: () => 'not_logged_in',
  loadUserProfile: () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session.user.id);
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        await loadUserProfile(session.user.id);
      } else {
        setUserProfile(null);
        setSubscription(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await authHelpers.getUserProfile(userId);
      if (error) throw error;
      setUserProfile(data);

      // Check subscription access
      const accessCheck = await authHelpers.checkSubscriptionAccess(userId, 'basic');
      setSubscription(accessCheck.subscription);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await authHelpers.signIn(email, password);
      if (error) throw error;
      
      if (data.user) {
        await loadUserProfile(data.user.id);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signUp = async (email, password, userData = {}) => {
    try {
      const { data, error } = await authHelpers.signUp(email, password, userData);
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Google sign in error:', error);
      return { data: null, error };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await authHelpers.signOut();
      if (error) throw error;
      setUser(null);
      setUserProfile(null);
      setSubscription(null);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const hasSubscriptionAccess = (requiredLevel = 'free') => {
    if (!user) return false;
    
    // Check if user is approved first
    if (userProfile?.approval_status !== 'approved') return false;
    
    if (requiredLevel === 'free') return true;
    if (!subscription) return false;

    const accessLevels = {
      'basic': ['Basic', 'Premium', 'Enterprise'],
      'premium': ['Premium', 'Enterprise'],
      'enterprise': ['Enterprise']
    };

    return accessLevels[requiredLevel]?.includes(subscription.planName) || false;
  };

  const getSubscriptionStatus = () => {
    if (!user) return 'not_logged_in';
    if (!subscription) return 'free';
    return subscription.planName?.toLowerCase() || 'free';
  };

  const value = {
    user,
    userProfile,
    subscription,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    hasSubscriptionAccess,
    getSubscriptionStatus,
    loadUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
